import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Callout,
  Drawer,
  DrawerSection,
  FailedCallout,
  ReasonField,
  Select,
  useAppNavigate,
  useReason,
  useToast,
  useCommandMutation,
} from "@amber/ui";
import type {
  MergeFieldChoices,
  MergeFieldValue,
  MergePreview as MergePreviewData,
  ProfileDetail,
  ProfileListRow,
} from "../../contracts/index";
import { useProfileInvalidation } from "../invalidation";
import { mergePreviewQuery, profileCommands } from "../queries";

const differs = (f: MergeFieldValue) => f.survivor !== f.other;

function keptValues(p: MergePreviewData, choices: MergeFieldChoices): string[] {
  return p.fields.filter(differs).map((f) => {
    const fromOther = choices[f.field] === "other";
    const value = (fromOther ? f.other : f.survivor) ?? "none";
    return `${f.label}: ${value}${fromOther ? ` (from ${p.other.name})` : ""}`;
  });
}

function MergePreview({ p, choices }: { p: MergePreviewData; choices: MergeFieldChoices }) {
  const kept = keptValues(p, choices);
  return (
    <DrawerSection title="3 · Preview">
      {p.blocked ? (
        <Callout tone="waiting">{p.blocked}</Callout>
      ) : (
        <ul className="m-0 list-disc pl-4 text-md">
          <li>
            {p.communications} communication{p.communications === 1 ? "" : "s"} with speaker attributions move to{" "}
            {p.survivor.name}; readiness is reassessed and consumers see a new version.
          </li>
          <li>
            {p.items} item{p.items === 1 ? "" : "s"} and {p.sources} source relationship
            {p.sources === 1 ? "" : "s"} move.
          </li>
          {kept.length > 0 ? <li>Values kept: {kept.join("; ")}.</li> : null}
          <li>Aliases added: {p.aliases_added.length > 0 ? p.aliases_added.join(", ") : "none"}.</li>
          <li>
            {p.other.name} closes; its link opens the surviving profile. History is kept in both, and the merge can be
            reversed.
          </li>
        </ul>
      )}
    </DrawerSection>
  );
}

function FieldChoices({
  p,
  choices,
  onChoose,
}: {
  p: MergePreviewData;
  choices: MergeFieldChoices;
  onChoose: (field: MergeFieldValue["field"], side: "survivor" | "other") => void;
}) {
  const open = p.fields.filter(differs);
  const same = p.fields.filter((f) => !differs(f) && f.survivor !== null);
  return (
    <div className="mt-2 flex flex-col gap-2">
      <p className="m-0 text-md text-muted">Choose which value survives for each field; aliases are combined.</p>
      {open.map((f) => (
        <fieldset key={f.field} className="m-0 border-0 p-0">
          <legend className="p-0 text-md font-medium">{f.label}</legend>
          {(["survivor", "other"] as const).map((side) => (
            <label key={side} className="flex h-[27px] items-center gap-2">
              <input
                type="radio"
                name={`field-${f.field}`}
                checked={(choices[f.field] ?? "survivor") === side}
                onChange={() => {
                  onChoose(f.field, side);
                }}
              />{" "}
              {f[side] ?? "None"} · {p[side].name}
            </label>
          ))}
        </fieldset>
      ))}
      {same.length > 0 ? (
        <p className="m-0 text-md text-muted">Same in both: {same.map((f) => f.label).join(", ")}.</p>
      ) : null}
    </div>
  );
}

export function MergePanel({
  profile,
  roster,
  onClose,
}: {
  profile: ProfileDetail;
  roster: readonly ProfileListRow[];
  onClose: () => void;
}) {
  const toast = useToast();
  const invalidation = useProfileInvalidation();
  const navigate = useAppNavigate();
  const candidates = useMemo(() => {
    const dupes = new Set(profile.duplicates.map((d) => d.id as string));
    return roster
      .filter((r) => r.id !== profile.id)
      .sort((a, b) => Number(dupes.has(b.id)) - Number(dupes.has(a.id)) || a.name.localeCompare(b.name));
  }, [roster, profile]);
  const [other, setOther] = useState<string>(profile.duplicates[0]?.id ?? "");
  const [keepThis, setKeepThis] = useState(true);
  const [picked, setPicked] = useState<{ pair: string; choices: MergeFieldChoices }>({ pair: "", choices: {} });
  const reason = useReason();
  const survivor = keepThis ? profile.id : other;
  const away = keepThis ? other : profile.id;
  const pair = `${survivor}:${away}`;
  const choices = picked.pair === pair ? picked.choices : {};
  const preview = useQuery({ ...mergePreviewQuery(survivor, away), enabled: other !== "" });
  const mutation = useCommandMutation({
    run: (csrf, text: string) =>
      profileCommands.merge(
        {
          survivor_id: survivor,
          other_id: away,
          survivor_revision: preview.data?.survivor.revision ?? 0,
          other_revision: preview.data?.other.revision ?? 0,
          field_choices: choices,
          reason: text,
        },
        csrf,
      ),
    ...invalidation,
    onSuccess: (r) => {
      const into = preview.data?.survivor.name ?? "the surviving profile";
      toast.show(
        r.state === "started"
          ? `Merge into ${into} started; it finishes when editing of an affected transcript ends`
          : `Merged into ${into}`,
      );
      onClose();
      navigate(`/profiles/${survivor}?tab=history`);
    },
  });
  const p = preview.data;
  const otherRow = roster.find((r) => r.id === other);
  return (
    <Drawer
      title="Merge with another profile"
      subtitle={profile.name}
      onClose={onClose}
      footer={
        <span className="flex items-center justify-end gap-2">
          <span className="mr-auto text-md text-muted">Nothing changes until you confirm.</span>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            type="submit"
            form="merge-form"
            disabledReason={!p ? "Choose the other profile first" : p.blocked}
            aria-busy={mutation.isPending || undefined}
          >
            Merge
          </Button>
        </span>
      }
    >
      <form
        id="merge-form"
        noValidate
        className="flex flex-col gap-4 text-ui"
        onSubmit={(e) => {
          e.preventDefault();
          if (reason.submit() && p && !p.blocked) mutation.mutate(reason.trimmed);
        }}
      >
        <DrawerSection title="1 · The other profile">
          <Select
            aria-label="Other profile"
            value={other}
            onChange={(e) => {
              setOther(e.target.value);
            }}
          >
            <option value="">Choose a profile…</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} · {c.state_label}
                {profile.duplicates.some((d) => d.id === c.id) ? " · possible duplicate" : ""}
              </option>
            ))}
          </Select>
        </DrawerSection>
        {other ? (
          <DrawerSection title="2 · Compare">
            <fieldset className="m-0 border-0 p-0">
              <legend className="p-0 text-md font-medium">Which profile stays</legend>
              <label className="flex h-[27px] items-center gap-2">
                <input
                  type="radio"
                  name="keep"
                  checked={keepThis}
                  onChange={() => {
                    setKeepThis(true);
                  }}
                />{" "}
                This profile stays ({profile.name} · {profile.state_label})
              </label>
              <label className="flex h-[27px] items-center gap-2">
                <input
                  type="radio"
                  name="keep"
                  checked={!keepThis}
                  onChange={() => {
                    setKeepThis(false);
                  }}
                />{" "}
                The other profile stays ({otherRow?.name ?? "—"} · {otherRow?.state_label ?? ""})
              </label>
            </fieldset>
            {p ? (
              <FieldChoices
                p={p}
                choices={choices}
                onChoose={(field, side) => {
                  setPicked({ pair, choices: { ...choices, [field]: side } });
                }}
              />
            ) : null}
          </DrawerSection>
        ) : null}
        {p ? <MergePreview p={p} choices={choices} /> : null}
        <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
        <FailedCallout error={mutation.error} className="" />
      </form>
    </Drawer>
  );
}
